package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.DesignAssetDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ShotPushState
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.shared.screenshots.templates.Template
import dev.lingvo.shared.screenshots.templates.TemplateCategory
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.screenshotCaptures
import dev.lingvo.site.api.screenshotDesign
import dev.lingvo.site.api.screenshotDesignAssets
import dev.lingvo.site.api.screenshotPlan
import dev.lingvo.site.api.screenshotPushJobs
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t

/** Category filtering is pure so deep links and toggle chips cannot disagree. */
class TemplatesModel(private val templates: List<Template>) {
    /** Unknown filters retire to All, matching screenshot URL-state rules. */
    fun filtered(category: String?): List<Template> {
        val selected = TemplateCategory.entries.firstOrNull { it.name.lowercase() == category } ?: return templates
        return templates.filter { selected in it.categories }
    }
}

internal data class DesignPageData(
    val project: ProjectDetailDto,
    val plan: ScreenshotPlanDto,
    val saved: ScreenshotDesignDto?,
    val design: ScreenshotDesign?,
    val captures: List<CaptureDto>,
    val captions: Map<String, List<KeyRow>>,
    val warnings: List<String>,
    val pushLocked: Boolean?,
    val artwork: List<DesignAssetDto> = emptyList(),
)

internal suspend fun loadDesignPage(project: ProjectDetailDto, platform: StorePlatform): DesignPageData {
    val plan = ApiClient.screenshotPlan(project.slug, platform)
    val saved = try { ApiClient.screenshotDesign(project.slug, platform) } catch (error: ApiException) {
        if (error.status == 404 && error.code == "design_not_found") null else throw error
    }
    val design = saved?.let { dto ->
        when (val decoded = DesignCodec.decodeElement(dto.document, StoreDeviceClassRule)) {
            is DecodeResult.Ok -> decoded.design
            is DecodeResult.Refused -> throw IllegalArgumentException(decoded.issues.joinToString("\n") { "${it.path}: ${it.message}" })
        }
    }
    val warnings = mutableListOf<String>()
    val captures = try { ApiClient.screenshotCaptures(project.slug, platform) } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (_: Throwable) {
        warnings += tr("app.projects.slug.screenshots.templates_model.captures_failed", "Captures couldn't load. Retry to see the latest images.")
        emptyList()
    }
    val captions = mutableMapOf<String, List<KeyRow>>()
    val file = project.files.firstOrNull { it.path == "store/${platform.id}/captions" }
    if (file != null) for (locale in (listOf(project.sourceLocale) + project.locales).distinct()) {
        try {
            val rows = mutableListOf<KeyRow>()
            var after: String? = null
            do {
                val response = ApiClient.listKeys(project.slug, locale = locale, fileId = file.id, limit = 100, after = after)
                rows += response.items
                val next = response.nextCursor
                check(next == null || next != after) { "The caption cursor did not advance." }
                after = next
            } while (after != null && rows.size < 1000)
            if (after != null) warnings += tr("app.projects.slug.screenshots.templates_model.caption_limit", "Some captions couldn't be loaded in this view. Open the captions file to check the complete set.")
            captions[locale] = rows
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            warnings += tr("app.projects.slug.screenshots.templates_model.captions_failed", "Some captions couldn't load. Retry to check every language.")
        }
    }
    val pushLocked = try {
        ApiClient.screenshotPushJobs(project.slug, platform).any {
            it.state in setOf(ShotPushState.QUEUED, ShotPushState.RUNNING, ShotPushState.WAITING_FOR_VERSION)
        }
    } catch (cancelled: CancellationException) { throw cancelled } catch (error: ApiException) {
        if (error.status == 404) false else {
            warnings += tr("app.projects.slug.screenshots.templates_model.push_status_failed", "The push status couldn't load. Editing is paused until it can be checked.")
            null
        }
    } catch (_: Throwable) {
        warnings += tr("app.projects.slug.screenshots.templates_model.push_status_failed", "The push status couldn't load. Editing is paused until it can be checked.")
        null
    }
    val artwork = try { ApiClient.screenshotDesignAssets(project.slug, platform) }
    catch (cancelled: CancellationException) { throw cancelled }
    catch (_: Throwable) {
        warnings += tr("app.projects.slug.screenshots.templates_model.project_artwork_couldnt_load_retry_to", "Project artwork couldn't load. Retry to see it in Canvas Assets.")
        emptyList()
    }
    return DesignPageData(project, plan, saved, design, captures, captions, warnings.distinct(), pushLocked, artwork)
}
